/**
 * What the self-echo detector compares (docs/plans/os-voice-conversation.md,
 * step 4.3; the rule Phase 0 measured in `scripts/spikes/self-echo.ts`).
 *
 * OS's own transcript and the transcriber disagree on form even when they
 * agree on the words: OS says "fourteen thousand two hundred fifty rupees"
 * or "चौदह हजार दो सौ पचास रुपये" where the transcriber writes "₹14,250", and
 * on hi-Latn OS speaks Roman while the transcriber writes Devanagari. So the
 * detector compares keys, not words: every number (digits, or number words
 * in English, Hindi and Hinglish) becomes one `#` (a run of them, one `#`),
 * currency words go, and every other word becomes a consonant skeleton that
 * Devanagari and Roman share ("बिक्री" and "bikri" are both `bkr`).
 */

const DEVANAGARI_DIGITS = '०१२३४५६७८९';

/** Words, normalised: lower case, NFKC, punctuation out, Devanagari digits read as ASCII, digit groups ("14,250") joined. */
export const echoTokensOf = (text: string): string[] => {
  let t = text.normalize('NFKC').toLowerCase();
  t = t.replace(/[०-९]/g, (d) => String(DEVANAGARI_DIGITS.indexOf(d)));
  t = t.replace(/(\d)[,\s](?=\d{2,3}\b)/g, '$1');
  t = t.replace(/[^\p{L}\p{M}\p{N}]+/gu, ' ');
  return t.split(' ').filter((word) => word !== '');
};

const NUMBER_WORDS = new Set(
  [
    'zero one two three four five six seven eight nine ten eleven twelve thirteen fourteen fifteen sixteen seventeen eighteen nineteen',
    'twenty thirty forty fifty sixty seventy eighty ninety hundred thousand million lakh lakhs crore crores',
    'एक दो तीन चार पांच पाँच छह छः छे सात आठ नौ दस ग्यारह बारह तेरह चौदह पंद्रह पन्द्रह सोलह सत्रह अठारह उन्नीस बीस',
    'इक्कीस बाईस तेईस चौबीस पच्चीस छब्बीस सत्ताईस अट्ठाईस उनतीस तीस इकतीस बत्तीस तैंतीस चौंतीस पैंतीस छत्तीस सैंतीस अड़तीस',
    'उनतालीस चालीस इकतालीस बयालीस तैंतालीस चवालीस पैंतालीस छियालीस सैंतालीस अड़तालीस उनचास पचास इक्यावन बावन तिरेपन चौवन',
    'पचपन छप्पन सत्तावन अट्ठावन उनसठ साठ इकसठ बासठ तिरेसठ चौंसठ पैंसठ छियासठ सड़सठ अड़सठ उनहत्तर सत्तर इकहत्तर बहत्तर',
    'तिहत्तर चौहत्तर पचहत्तर छिहत्तर सतहत्तर अठहत्तर उनासी अस्सी इक्यासी बयासी तिरासी चौरासी पचासी छियासी सत्तासी अट्ठासी',
    'नवासी नब्बे इक्यानवे बानवे तिरानवे चौरानवे पचानवे छियानवे सत्तानवे अट्ठानवे निन्यानवे सौ हजार हज़ार लाख करोड़',
    'ek teen char chaar paanch panch chhe chhah saat aath nau das gyarah barah baarah terah chaudah pandrah solah satrah atharah unnis bees',
    'tees chalis pachas pachaas saath sattar assi nabbe sau hazaar hazar hajar lakh crore karod',
  ]
    .join(' ')
    .normalize('NFKC')
    .split(' '),
);
const JOINED_NUMBER = /^(twenty|thirty|forty|fifty|sixty|seventy|eighty|ninety)(one|two|three|four|five|six|seven|eight|nine)$/;
const CURRENCY = new Set(['rupees', 'rupee', 'rupaye', 'rupaiye', 'rupay', 'rs', 'inr', 'रुपये', 'रुपए', 'रुपया', 'रुपयों']);

const DEVANAGARI_CONSONANTS: Readonly<Record<string, string>> = {
  क: 'k', ख: 'k', ग: 'g', घ: 'g', ङ: 'n', च: 'c', छ: 'c', ज: 'j', झ: 'j', ञ: 'n',
  ट: 't', ठ: 't', ड: 'd', ढ: 'd', ण: 'n', त: 't', थ: 't', द: 'd', ध: 'd', न: 'n',
  प: 'p', फ: 'p', ब: 'b', भ: 'b', म: 'm', य: 'y', र: 'r', ल: 'l', व: 'v',
  श: 's', ष: 's', स: 's', ह: 'h', 'ं': 'n', ऋ: 'r',
};
const NUKTA = '़';
const WITH_NUKTA: Readonly<Record<string, string>> = { ड: 'r', ढ: 'r', ज: 'j', फ: 'p', क: 'k', ख: 'k', ग: 'g' };

/** A word as its consonants, the same for Devanagari and Roman: "बिक्री", "bikri" → "bkr"; "टोटल", "total" → "tl". */
export const skeletonOf = (word: string): string => {
  let out = '';
  if (/[ऀ-ॿ]/.test(word)) {
    const chars = [...word.normalize('NFD')];
    for (let at = 0; at < chars.length; at += 1) {
      const char = chars[at] ?? '';
      const nukta = WITH_NUKTA[char];
      if (chars[at + 1] === NUKTA && nukta !== undefined) {
        out += nukta;
        at += 1;
      } else {
        out += DEVANAGARI_CONSONANTS[char] ?? (/[a-z0-9]/.test(char) ? char : '');
      }
    }
  } else {
    out = word
      .replace(/ch/g, 'C')
      .replace(/(ph|f)/g, 'p')
      .replace(/(kh|ck|q)/g, 'k')
      .replace(/gh/g, 'g')
      .replace(/sh/g, 's')
      .replace(/th/g, 't')
      .replace(/dh/g, 'd')
      .replace(/bh/g, 'b')
      .replace(/(jh|z)/g, 'j')
      .replace(/w/g, 'v')
      .replace(/x/g, 'ks')
      .replace(/c/g, 'k')
      .replace(/C/g, 'c')
      .replace(/(.)y$/, '$1')
      .replace(/[aeiou]/g, '');
  }
  return out.replace(/(.)\1+/g, '$1');
};

/** What the detector compares: numbers as one `#` a run, currency words gone, every other word as its skeleton. */
export const echoKeysOf = (text: string): string[] => {
  const out: string[] = [];
  for (const word of echoTokensOf(text)) {
    if (CURRENCY.has(word)) continue;
    if (/^\d+$/.test(word) || NUMBER_WORDS.has(word) || JOINED_NUMBER.test(word)) {
      if (out.at(-1) !== '#') out.push('#');
      continue;
    }
    const key = skeletonOf(word);
    if (key !== '') out.push(key);
  }
  return out;
};

/** The longest run of consecutive keys of `a` that also occurs, consecutive and in order, in `b`. */
export const longestCommonRun = (a: readonly string[], b: readonly string[]): number => {
  let best = 0;
  let prev = new Array<number>(b.length + 1).fill(0);
  for (let i = 1; i <= a.length; i += 1) {
    const row = new Array<number>(b.length + 1).fill(0);
    for (let j = 1; j <= b.length; j += 1) {
      if (a[i - 1] === b[j - 1]) {
        const run = (prev[j - 1] ?? 0) + 1;
        row[j] = run;
        if (run > best) best = run;
      }
    }
    prev = row;
  }
  return best;
};
